import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import useSencoPupils from '../../hooks/useSencoPupils';
import { PATHWAY_LABELS } from '../../utils/triagePathways';
import { FAMILY_SERVICE_NAME, buildFamilyChild, familyUserName, letterEmbedUrl } from '../../utils/familyPortal';
import FamilyPortalShell from './FamilyPortalShell';
import FamilyChildCard from './FamilyChildCard';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import useIsDesktop from './useIsDesktop';
import LetterLeaflets from './LetterLeaflets';
import {
  FamilyPathwayPageHead,
  FamilyRecordStates,
  letterSharedWithGuardian,
} from './FamilyReferralPage';
import '../../components/harbour/harbour.css';
import './familyReferral.css';

/*
 * Family portal · outcome letter viewer (TDI-53, parent-or-carer side).
 *
 * Mobile (440 frames): the letter on the paper band with a sticky Download
 * bar below the scroll area (the gold primary is the one main action).
 *
 * Desktop (1720 frames): the letter opens as a wide task flyout over Your
 * children (Harbour/Flyout, width 1688) — referral eyebrow and the child's
 * name in the header, the letter on a white card in a centred 760 column,
 * and the gold Download on the submit bar under the scroll area.
 *
 * On every width the letter is fetched through the same guarded signed-URL
 * callable the legacy service-user overview uses (TD-145): the backend only
 * serves it once a clinician has sent the letter to the guardian role.
 */
const GUARDIAN_USER_TYPE = 'RMS Legal Guardian';

export default function FamilyLetterPage() {
  const navigate = useNavigate();
  const { patientRecordId, pathwayKey } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { pupils, loading, error, reload } = useSencoPupils({ orgId, claimsOrgId });
  const isDesktop = useIsDesktop();

  // 'loading' | 'ready' | 'error' for the letter itself (the signed URL).
  const [letterState, setLetterState] = useState('loading');
  const [letterUrl, setLetterUrl] = useState(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const pupil = pupils.find((p) => p.id === patientRecordId) || null;
  const patient = pupil?.patient || {};
  const pathwayLabel = PATHWAY_LABELS[pathwayKey] || '';
  const letterAvailable = Boolean(pupil) && letterSharedWithGuardian(patient, pathwayKey);
  const child = useMemo(() => buildFamilyChild(pupil), [pupil]);
  const allChildren = useMemo(() => pupils.map(buildFamilyChild).filter(Boolean), [pupils]);

  const fetchSignedUrl = useCallback(
    (disposition) => {
      const fn = httpsCallable(functions, 'rmsFetchTriageDocument', { timeout: 30000 });
      return fn({
        orgId,
        patientRecordId,
        fileFormat: 'pdf',
        disposition,
        pathway: pathwayKey,
      });
    },
    [orgId, patientRecordId, pathwayKey]
  );

  // Fetch the inline view once the record says the letter has been shared.
  useEffect(() => {
    if (!letterAvailable) return undefined;
    let cancelled = false;
    (async () => {
      setLetterState('loading');
      try {
        const res = await fetchSignedUrl('inline');
        const url = res?.data?.downloadUrl;
        if (!cancelled) {
          if (url) {
            setLetterUrl(url);
            setLetterState('ready');
          } else {
            setLetterState('error');
          }
        }
      } catch (err) {
        if (!cancelled) setLetterState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [letterAvailable, fetchSignedUrl, fetchAttempt]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetchSignedUrl('attachment');
      const url = res?.data?.downloadUrl;
      if (url) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch (err) {
      console.error('Failed to download the outcome letter:', err);
      // A click that quietly does nothing reads as a broken page. Fall back to
      // the error state, which offers Try again.
      setLetterState('error');
    } finally {
      setDownloading(false);
    }
  };

  // This page is the parent-or-carer view only.
  if (claims && claims.userType !== GUARDIAN_USER_TYPE) {
    return <Navigate to="/rms/dashboard" replace />;
  }

  const userName = familyUserName(user, pupils) || 'Account';
  const back = { to: `/rms/family/child/${patientRecordId}`, label: 'Back' };

  if (loading || error || !pupil || !pathwayLabel) {
    return (
      <FamilyPortalShell userName={userName} back={back}>
        <FamilyRecordStates
          loading={loading && !error}
          title="The letter could not be loaded"
          onRetry={() => reload({ force: true })}
        />
      </FamilyPortalShell>
    );
  }

  const meta = `From: ${pathwayLabel} Service, ${FAMILY_SERVICE_NAME}`;

  // The letter's own states, identical on both widths (only the surface the
  // letter sits on changes: paper band on mobile, white card on desktop).
  const letterStates = (
    <>
      {letterState === 'loading' && (
        <div className="family-letter-loading animate-pulse" aria-hidden="true">
          <div className="harbour-skeleton" />
          <div className="harbour-skeleton" />
          <div className="harbour-skeleton" />
        </div>
      )}
      {letterState === 'error' && (
        <div className="family-page-error">
          {/* Nothing was being entered here, so the old reassurance about
              unsaved answers only raised a worry of its own. */}
          <HarbourNotice kind="error" title="The letter could not be loaded" />
          <HarbourButton kind="secondary" onClick={() => setFetchAttempt((n) => n + 1)}>
            Try again
          </HarbourButton>
        </div>
      )}
      {letterState === 'ready' && (
        <>
          <iframe title="Outcome letter" src={letterEmbedUrl(letterUrl)} className="family-letter-frame" />
          <LetterLeaflets patient={patient} pathwayKey={pathwayKey} />
        </>
      )}
    </>
  );

  // Offering a download of a letter that is not there is a promise the page
  // cannot keep, so the bar below only appears once the letter has loaded.
  const letterReady = letterState === 'ready';
  const downloadButton = (block) => (
    <HarbourButton
      kind="family"
      leadingIcon="download"
      className={block ? 'family-btn-block' : undefined}
      busy={downloading}
      aria-busy={downloading}
      disabled={downloading}
      onClick={handleDownload}
    >
      Download
    </HarbourButton>
  );

  // ── Desktop: wide task flyout over Your children ──────────────────────────
  if (isDesktop) {
    return (
      <FamilyPortalShell userName={userName} maxWidth="full">
        <div className="family-page-head">
          <h1 className="family-page-head__title family-page-head__title--home">Your children</h1>
          <p className="family-page-head__intro">Choose your child’s name to see their details and questions.</p>
        </div>
        <ul className="family-child-cards">
          {allChildren.map((c) => (
            <li key={c.id}><FamilyChildCard child={c} linkState={{ stay: true }} /></li>
          ))}
        </ul>

        <HarbourFlyout
          open
          width="wide"
          eyebrow={{ tone: 'referral', label: 'Referral pathway' }}
          title={child?.name || pupil.name}
          year={child?.meta}
          onClose={() => navigate(`/rms/family/child/${patientRecordId}`, { state: { stay: true } })}
          closeLabel="Close the letter"
        >
          <div className="family-letter-flyout-body">
            <div className="family-letter-column">
              <div className="family-letter-card">
                <div className="family-letter-card__head">
                  <h2 className="harbour-heading-24 family-letter-card__title">
                    {`Outcome letter: ${pathwayLabel} referral`}
                  </h2>
                  <p className="harbour-body-16 family-letter-card__meta">{meta}</p>
                </div>
                {!letterAvailable ? (
                  <HarbourNotice kind="info" title="The outcome letter is not available yet." />
                ) : (
                  letterStates
                )}
              </div>
            </div>
          </div>
          {letterAvailable && letterReady && (
            <div className="harbour-submit-bar">{downloadButton(false)}</div>
          )}
        </HarbourFlyout>
      </FamilyPortalShell>
    );
  }

  // ── Mobile: the letter on the paper band ──────────────────────────────────
  return (
    <FamilyPortalShell userName={userName} back={back}>
      <div className="family-path-stack">
        <FamilyPathwayPageHead title={`Outcome letter: ${pathwayLabel} referral`} meta={meta} />

        {!letterAvailable ? (
          <HarbourNotice kind="info" title="The outcome letter is not available yet." />
        ) : (
          <>
            <div className="family-letter-paper">{letterStates}</div>
            {letterReady && <div className="family-letter-bar">{downloadButton(true)}</div>}
          </>
        )}
      </div>
    </FamilyPortalShell>
  );
}
